import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  AppDialog,
  AppDialogActions,
  AppDialogCancelButton,
  AppDialogContent,
  AppDialogTitle,
} from "../dialog";
import type { DrillDownResponse } from "../../services/dashboardService";
import { useAppCustomStyles } from "../../assets/theme/custom/hooks";

type Props = {
  open: boolean;
  loading?: boolean;
  data?: DrillDownResponse | null;
  onClose: () => void;
};

export default function DrillDownDialog({ open, loading, data, onClose }: Props) {
  const { t } = useTranslation();
  const custom = useAppCustomStyles();

  return (
    <AppDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <AppDialogTitle>
        {t("drillDown.title", {
          field: data?.field || "—",
          value: data?.value || "—",
        })}
      </AppDialogTitle>
      <AppDialogContent>
        {loading && (
          <Box sx={custom.components.drillDownDialog.loadingCenter}>
            <CircularProgress size={28} />
          </Box>
        )}
        {!loading && data && (
          <>
            <Typography variant="caption" color="text.secondary" sx={custom.components.drillDownDialog.subtitle}>
              {t("drillDown.subtitle", { count: data.totalRows, index: data.index })}
            </Typography>
            <Box sx={custom.components.drillDownDialog.tableBox}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {data.columns.map((col) => (
                      <TableCell key={col}>{col}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows.map((row, idx) => (
                    <TableRow key={idx}>
                      {data.columns.map((col) => (
                        <TableCell key={col}>
                          {row[col] != null ? String(row[col]) : "—"}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  {!data.rows.length && (
                    <TableRow>
                      <TableCell colSpan={data.columns.length || 1}>
                        {t("dashboard.noWidgetData")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </AppDialogContent>
      <AppDialogActions>
        <AppDialogCancelButton onClick={onClose}>{t("common.cancel")}</AppDialogCancelButton>
      </AppDialogActions>
    </AppDialog>
  );
}
